import React, { useEffect } from "react";

import { useResolvePendingItemMutation, useGetCurrentCompanyApplicationQuery } from "EmployerApplication/api";
import { addEmployeesURL } from "EmployerApplication/urls";
import createDecorator from "final-form-focus";
import { Form } from "react-final-form";
import { useHistory } from "react-router-dom";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import { CardSection } from "Common/Card";
import { SIFinalFormField, SIFormRadioGroup } from "Common/forms";
import { SaveButton } from "Common/forms/buttons";
import { Row, Col } from "Common/Grid";
import { Div } from "Common/HTML";
import { Body2 } from "Theme/BodyText";
import Button from "Theme/Button";

import makeBem from "Utils/makeBem";

const bem = makeBem("ApplicationStepCard");
const focusOnErrors = createDecorator();

const DentalMinimum = ({ dentalCarrierName, pendingItemPayload, scrollToEmployeeApplications }) => {
  const history = useHistory();
  const { data: companyApplication } = useGetCurrentCompanyApplicationQuery();
  const companyPolicyId = companyApplication?.id;
  const [resolvePendingItem] = useResolvePendingItemMutation();
  useEffect(() => {
    AnalyticsTracking.trackEvent("pending_item_dental_minimum");
  }, []);

  const dentalMinimumLabel = (
    <Div className={bem("PendingItemsLabel")}>
      For dental insurance only, {dentalCarrierName} requires that at least {pendingItemPayload.dental_minimum_enrollment_requirement} person(s) enroll in
      coverage.
    </Div>
  );

  const dentalMinimumOptions = {
    removeDentalCoverage: "I will move forward without dental coverage",
    modifyEmployeesEnrollingInDental: "I have another employee who will enroll in coverage",
  };

  const submitHandler = async () => {
    if (!companyPolicyId) return;
    return resolvePendingItem({
      companyPolicyId,
      type: "dental_minimum",
      resolution_type: "remove_dental_coverage",
    });
  };

  const onAddClicked = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "dental_minimum_page",
      action: "add_new_enrollee",
    });

    history.push(addEmployeesURL);
  };

  const onEditClicked = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "dental_minimum_page",
      action: "enroll_existing_employee",
    });

    scrollToEmployeeApplications();
  };

  return (
    <Form decorators={[focusOnErrors]} onSubmit={submitHandler}>
      {({ handleSubmit, submitting, values }) => (
        <form onSubmit={handleSubmit}>
          <Row>
            <SIFinalFormField
              name="dentalMinimumResolution"
              label={dentalMinimumLabel}
              component={SIFormRadioGroup}
              componentProps={{
                options: dentalMinimumOptions,
              }}
              className={bem(null, "pendingItemRadios")}
            />
          </Row>
          {values.dentalMinimumResolution == "modifyEmployeesEnrollingInDental" && (
            <Row>
              <SIFinalFormField
                name="dentalMinimumModifyEmployeesResolution"
                component={SIFormRadioGroup}
                label="You can add another employee, or edit the application of an employee who previously declined coverage."
                componentProps={{
                  options: {
                    addEmployee: "I will add another employee who will enroll in coverage",
                    enrollMoreEmployees: "An employee who previously declined coverage will enroll",
                  },
                }}
                className={bem(null, "pendingItemRadios")}
              />

              {values.dentalMinimumModifyEmployeesResolution == "addEmployee" && (
                <CardSection>
                  <Button color="gray" size="small" style={{ minWidth: "258px" }} onClick={onAddClicked}>
                    Add a New Enrollee
                  </Button>
                </CardSection>
              )}

              {values.dentalMinimumModifyEmployeesResolution == "enrollMoreEmployees" && (
                <CardSection>
                  <Button color="gray" size="small" style={{ minWidth: "258px" }} onClick={onEditClicked}>
                    Edit Existing Employee Applications
                  </Button>
                </CardSection>
              )}
            </Row>
          )}

          <Body2 mb={4}>You can always sign up for just medical insurance now, and add dental coverage at a later date when you have more employees.</Body2>
          <Row>
            <Col>
              <SaveButton buttonText="Submit" submitting={submitting} disabled={values.dentalMinimumResolution != "removeDentalCoverage"} size="small" mt={3} />
            </Col>
          </Row>
        </form>
      )}
    </Form>
  );
};

export default DentalMinimum;
